import gql from 'graphql-tag';

export const QUERY_LANDLORD_ORDERS = gql`
  query queryLandlordOrders(
    $pageNumber: PositiveInteger = 1
    $pageSize: PositiveInteger = 10
    $stages: [OrderStage]
    $referenceId: String
    $passportFullName: NonEmptyString
    $sortBy: OrderSortBy = UPDATED_AT
    $sortDirection: SortDirection = DESCENDING
    $propertyIds: [ID]
    $createdAfter: VerifyDate
  ) {
    viewer {
      ... on LandlordContact {
        company {
          pendingConfirmationOrderCount: orders(
            stages: [WAITING_FOR_CONFIRMATION]
            createdAfter: $createdAfter
            bookingJourneys: [SEMI_AUTOMATIC]
          ) {
            totalCount
          }
          pendingRefundOrderCount: orders(
            stages: [REFUNDING]
            bookingJourneys: [SEMI_AUTOMATIC]
            createdAfter: $createdAfter
          ) {
            totalCount
          }
          orders(
            pageNumber: $pageNumber
            pageSize: $pageSize
            stages: $stages
            referenceId: $referenceId
            passportFullName: $passportFullName
            sortBy: $sortBy
            sortDirection: $sortDirection
            propertyIds: $propertyIds
            bookingJourneys: [SEMI_AUTOMATIC]
          ) {
            totalCount
            edges {
              node {
                billingCycle
                currency
                id
                moveInDate
                moveOutDate
                propertyId
                propertyName
                referenceId
                stage
                student {
                  firstName
                  id
                  lastName
                  passportFirstName
                  passportLastName
                  passportMiddleName
                }
                totalPrice
                unitTypeCategory
                unitTypeId
                listingId
                listingSalesPrice
                listingDiscountValue
                unitTypeName
                secondaryStage
                instalmentPlanType
                counterOffers {
                  id
                  moveInDate
                  moveOutDate
                  unitTypeName
                  unitTypeCategory
                  totalPrice
                  currency
                  property {
                    id
                    name
                  }
                }
                property {
                  id
                  name
                  city {
                    slug
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const QUERY_LANDLORD_BOOKINGS = gql`
  query queryLandlordBookings(
    $pageNumber: PositiveInteger = 1
    $pageSize: PositiveInteger = 10
    $referenceId: String
    $passportFullName: NonEmptyString
    $sortBy: BookingSortBy = UPDATED_AT
    $sortDirection: BookingSortDirection = DESCENDING
    $propertyIds: [ID]
    $opportunityStages: [LandlordOpportunityStage!]!
  ) {
    viewer {
      ... on LandlordContact {
        company {
          bookings(
            pageNumber: $pageNumber
            pageSize: $pageSize
            referenceId: $referenceId
            sortBy: $sortBy
            sortDirection: $sortDirection
            propertyIds: $propertyIds
            passportFullName: $passportFullName
            opportunityStages: $opportunityStages
          ) {
            totalCount
            edges {
              node {
                currency
                id
                moveInDate
                moveOutDate
                opportunity {
                  bookingJourney
                  completedXbooking {
                    billingCycle
                    listingMoveIn
                    listingMoveInType
                    listingMoveOut
                    listingMoveOutType
                    propertyName
                    referenceId
                    roomName
                    unitPrice
                    commission {
                      invoiceNumber
                      bonusValue
                      currency
                      totalCommissionValue
                      value
                    }
                  }
                  currency
                  finalMoveInDate
                  finalMoveOutDate
                  finalPrice
                  id
                  stage
                  landlordBookingStatus
                  student {
                    dateOfBirth
                    destinationUniversity
                    emailAddress
                    gender
                    nationality
                    passportFirstName
                    passportLastName
                    passportMiddleName
                    passportNumber
                    phoneNumber
                  }
                }
                price
                propertyName
                referenceId
                student {
                  firstName
                  id
                  lastName
                  passportFirstName
                  passportLastName
                  passportMiddleName
                }
                roomName
                roomType
              }
            }
          }
        }
      }
    }
  }
`;

export const QUERY_INSTANT_ORDERS = gql`
  query queryInstantOrders(
    $pageNumber: PositiveInteger = 1
    $pageSize: PositiveInteger = 10
    $stages: [OrderStage]
    $referenceId: String
    $passportFullName: NonEmptyString
    $sortBy: OrderSortBy = UPDATED_AT
    $sortDirection: SortDirection = DESCENDING
    $propertyIds: [ID]
  ) {
    viewer {
      ... on LandlordContact {
        company {
          orders(
            pageNumber: $pageNumber
            pageSize: $pageSize
            stages: $stages
            referenceId: $referenceId
            passportFullName: $passportFullName
            sortBy: $sortBy
            sortDirection: $sortDirection
            propertyIds: $propertyIds
            bookingJourneys: [INSTANT_BOOK]
          ) {
            totalCount
            edges {
              node {
                currency
                id
                moveInDate
                moveOutDate
                propertyName
                referenceId
                stage
                student {
                  firstName
                  id
                  lastName
                  passportFirstName
                  passportLastName
                  passportMiddleName
                }
                totalPrice
                unitTypeCategory
                unitTypeId
                unitTypeName
              }
            }
          }
        }
      }
    }
  }
`;

export const MUTATION_CONFIRM_ORDER = gql`
  mutation mutationConfirmOrder($input: ConfirmOrderInput) {
    confirmOrder(input: $input) {
      order {
        id
      }
    }
  }
`;

export const MUTATION_REJECT_ORDER = gql`
  mutation mutationRejectOrder($input: RejectOrderInput) {
    rejectOrder(input: $input) {
      order {
        id
      }
    }
  }
`;

export const QUERY_BOOKINGS_STATISTICS = gql`
  query queryBookingsStatistics($createdAfter: VerifyDate, $propertyIds: [ID]) {
    viewer {
      ... on LandlordContact {
        allProperties: properties(pageSize: 9999, pageNumber: 1) {
          edges {
            node {
              name
              id
            }
          }
        }
        instantProperty: properties(
          pageSize: 9999
          pageNumber: 1
          bookingJourneys: [INSTANT_BOOK]
        ) {
          totalCount
        }
        manualProperty: properties(
          pageSize: 9999
          pageNumber: 1
          bookingJourneys: [MANUAL, FULL_AUTOMATIC]
        ) {
          totalCount
        }
        herculesProperty: properties(
          pageSize: 9999
          pageNumber: 1
          bookingJourneys: [SEMI_AUTOMATIC]
        ) {
          totalCount
        }
        company {
          herculesWeeklyOrders: orders(
            createdAfter: $createdAfter
            stages: [WAITING_FOR_CONFIRMATION]
            propertyIds: $propertyIds
            bookingJourneys: [SEMI_AUTOMATIC]
          ) {
            totalCount
          }
          herculesAllOrders: orders(propertyIds: $propertyIds, bookingJourneys: [SEMI_AUTOMATIC]) {
            totalCount
          }
          instantAllOrders: orders(propertyIds: $propertyIds, bookingJourneys: [INSTANT_BOOK]) {
            totalCount
          }
          instantWeeklyOrders: orders(
            createdAfter: $createdAfter
            stages: [SIGNING_AGREEMENT, MAKING_PAYMENT, FILLING_PERSONAL_INFO]
            propertyIds: $propertyIds
            bookingJourneys: [INSTANT_BOOK]
          ) {
            totalCount
          }
          manualAllBookings: bookings(
            propertyIds: $propertyIds
            opportunityStages: [COMPLETED, CANCELLATION]
          ) {
            totalCount
          }
        }
      }
    }
  }
`;

export const QUERY_UNIT_TYPES = gql`
  query propertyUnitTypes($propertyId: ID) {
    listUnitTypes(propertyId: $propertyId) {
      edges {
        node {
          id
          name
          category
          floors
          roomSize
          roomType
          bathroomType
          viewType
          kitchenArrangement
          bedCount
          bedroomCountMin
          bedroomCountMax

          genderMix
          smokingPreference
          dietaryPreference

          unitTypeBedSizes {
            type
            length
            width
            bedCount
          }

          listings(journey: SEMI_AUTOMATIC) {
            id
            type
            moveInType
            moveOutType
            moveIn
            moveOut
            tenancyLengthType
            tenancyLengthValue
            availability
            state
            priceMin
            priceMax
            discountValue
            discountType
            availableThoughMoveInDatePassed
          }

          price {
            isFrom
            minPrice
            discountedMinPrice
          }
        }
      }
    }
  }
`;

export const QUERY_ORDER_PRICE = gql`
  query calculateOrderPrice(
    $listingId: ID!
    $manualDiscountType: DiscountType
    $manualDiscountValue: String
    $moveInDate: VerifyDate!
    $moveOutDate: VerifyDate!
  ) {
    calculateOrderPrice(
      listingId: $listingId
      manualDiscountType: $manualDiscountType
      manualDiscountValue: $manualDiscountValue
      moveInDate: $moveInDate
      moveOutDate: $moveOutDate
    ) {
      nonInstalments {
        billingCycle
        listingSalesOriginPrice
        listingSalesPrice
        totalPrice
      }
    }
  }
`;

export const CREATE_COUNTER_OFFER = gql`
  mutation createOrderCounterOffer($input: CreateOrderCounterOfferInput) {
    createOrderCounterOffer(input: $input) {
      counterOffer {
        id
        listingId
      }
    }
  }
`;

export const CHECK_COUNTER_OFFER_INSTALMENT = gql`
  query checkOrderCounterOfferInstalment($input: CheckOrderCounterOfferInstalmentInput) {
    checkOrderCounterOfferInstalment(input: $input) {
      result
    }
  }
`;

export const QUERY_LANDLORD_PROPERTIES = gql`
  query queryLandlordProperties(
    $name: String
    $citySlug: String
    $pageSize: Int! = 10
    $pageNumber: Int! = 1
  ) {
    properties(
      name: $name
      citySlug: $citySlug
      sortBy: STATUS_NAME
      statuses: [EDITING, PUBLISHED, UNPUBLISHED]
      bookingJourneys: [SEMI_AUTOMATIC]
      pageNumber: $pageNumber
      pageSize: $pageSize
    ) {
      totalCount
      edges {
        node {
          name
          slug
          id
          state
          status
        }
      }
    }
  }
`;
